import React, { useState, useEffect, useContext } from 'react';
import '../Project.less';
import { Col, Form, Row } from 'react-bootstrap';
import moment from 'moment';
import BackBtn from '../../components/UI/BackBtn';
import useUserContext from '../../hooks/useUserContext';
import Required from '../../components/Required';
import { years } from '../../helpers';
import DownloadLink from '../../components/common/DownloadLink';
import Page from '../../components/_layout/Page';
import useAPI from '../../hooks/useAPI';
import ProgramBuysTableBuys from './tabsAccounting/ProgramBuysTableBuys';
import { isAdmin, isAdminRead } from '../../services/rolesSvc';
import CofinancingBudgetTable from './tabsAccounting/CofinancingBudgetTable';
import ExtraBudgetTable from './tabsAccounting/ExtraBudgetTable';
import AttractedBudgetTable from './tabsAccounting/AttractedBudgetTable';
import StrategiesBudgetTable from './tabsAccounting/StrategiesBudgetTable';

const reportTypes = {
  byStrategies: { title: 'В разрезе СТП/СЦ', url: '/reports/budget/byStrategies' },
  byProgramBuys: { title: 'По закупкам Программы', url: '/reports/budget/buys', show: (user) => isAdminRead(user) },
  cofinancing: { title: 'Отчет по софинансированию из собственных средств', url: '/reports/budget/buys' },
  extra: { title: 'Собственный внебюджет', url: '/reports/budget/funds/internal' },
  attracted: { title: 'Привлеченный внебюджет', url: '/reports/budget/funds/incoming' },
  allLevels: { title: 'Бюджеты всех уровней', url: '/reports/budget/funds/all' },
  minobr: { title: 'Отчет о привлеченных средствах на выполнение НИОКР', url: '/reports/budget/funds/aggregated' },
  byOrgsCategories: { title: 'Отчет о привлеченных средствах на выполнение НИОКР (по категориям организаций) - 2024', url: '/reports/budget/funds/byOrgsCategories' },
};

export default function BudgetReports() {
  const userCtx = useUserContext();
  const api = useAPI();
  // todo: multiple choice
  const [selectedYears, setSelectedYears] = useState([new Date().getFullYear()]);
  const [selectedReport, setReport] = useState('byStrategies');
  const [selectedFinancingType, setSelectedFinancingType] = useState('fb');
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  useEffect(async () => {
    if (selectedReport === 'byProgramBuys') {
      setIsLoading(true);
      const response = await api.get(`${reportTypes.byProgramBuys.url}?year=${selectedYears[0]}`);
      if (response && !response.error) {
        setTableData(response);
      }
      setIsLoading(false);
    } else {
      setTableData([]);
    }
  }, [selectedYears, selectedReport]);

  const getDownloadUrl = () => {
    let url = `${reportTypes[selectedReport]?.url}/export?year=${selectedYears[0]}`
    if(selectedReport === 'cofinancing'){
      url += '&type=sf';
    } else if(selectedReport === 'byStrategies'){
      url += `&type=${selectedFinancingType}`;
    }
    return url;
  }

  const getExportFileName = () => {
    const timestampString = `от ${moment().format('DD.MM.YYYY')}`;
    const financingTypeLabel = (selectedFinancingType && selectedReport === 'byStrategies') ? ` (${selectedFinancingType === 'fb' ? 'грант' : 'СФ'})` : '';
    return `${reportTypes[selectedReport]?.title}-${selectedYears.join('-')}${financingTypeLabel} ${timestampString}.xlsx`;
  };

  return (
    <Page className="project-page">
      <BackBtn />
      <h3 style={styledHeading}>Отчёты по бюджету Программы</h3>
      <div style={{ marginTop: 20 }}>
        <Row className="mb-12">
          <Form.Group as={Col} md="2">
            <Form.Label>
              Отчетный год
              <Required />
            </Form.Label>
            <Form.Control
              required
              as="select"
              value={selectedYears[0]}
              onChange={(e) => setSelectedYears([parseInt(e.target.value)])}
            >
              {years.map((year) => <option key={year} value={year}>{year}</option>)}
            </Form.Control>
            <Form.Control.Feedback type="invalid">Выберите год</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="4">
            <Form.Label>
              Тип отчета
              <Required />
            </Form.Label>
            <Form.Control
              required
              as="select"
              value={selectedReport}
              onChange={(e) => setReport(e.target.value)}
            >
              <option value="" />
              {Object.entries(reportTypes)
                .filter(([key, type]) => !type.show || type.show(userCtx))
                .map(([key, type]) => <option key={key} value={key}>{type.title}</option>)}
            </Form.Control>
            <Form.Control.Feedback type="invalid">Выберите тип отчета</Form.Control.Feedback>
          </Form.Group>
          {selectedReport === 'byStrategies' && <Form.Group as={Col} md="4">
            <Form.Label>
              Тип финансирования
              <Required />
            </Form.Label>
            <Form.Control
              required
              as="select"
              value={selectedFinancingType}
              onChange={(e) => setSelectedFinancingType(e.target.value)}
            >
              <option value="fb">Грант</option>
              <option value="sf">Софинансирование</option>
            </Form.Control>
          </Form.Group>}
          <Form.Group as={Col}>
            <DownloadLink
              variant="primary"
              type="submit"
              url={getDownloadUrl()}
              style={{ marginTop: '30px' }}
              filename={getExportFileName()}
            >
              Выгрузить в Excel
            </DownloadLink>
          </Form.Group>
        </Row>
        <Row>
          {
            isLoading && <div className="mt-2">Загрузка...</div>
          }
          {
            !isLoading && selectedReport === 'byStrategies' && (
              <div className='mt-4'>
                <StrategiesBudgetTable year={selectedYears[0]} financingType={selectedFinancingType}/>
              </div>
            )
          }
          {
            !isLoading && selectedReport === 'byProgramBuys' && (
            <ProgramBuysTableBuys
              data={tableData}
              readonly
              year={selectedYears[0]}
            />
            )
          }
          {
            !isLoading && selectedReport === 'cofinancing' && (
              <CofinancingBudgetTable year={selectedYears[0]}/>
            )
          }
          {
            !isLoading && selectedReport === 'extra' && (
              <ExtraBudgetTable year={selectedYears[0]}/>
            )
          }
          {
            !isLoading && selectedReport === 'attracted' && (
              <AttractedBudgetTable year={selectedYears[0]} type='attracted'/>
            )
          }
          {
            !isLoading && selectedReport === 'allLevels' && (
              <AttractedBudgetTable year={selectedYears[0]} type='allLevels'/>
            )
          }
        </Row>
      </div>
    </Page>
  );
}

const styledHeading = { marginLeft: 0, marginBottom: 24, marginTop: 24 };
